import { calculateAngle } from "../utils/geom";
import { getBaseEditStyle, getDefaultEditStylePoint } from "./styles/defaults";
import { getTextBaseStyle } from "./styles/text";
import { ol_VectorLayer, ol_Coordinate, ol_extent, ol_Feature, ol_Map, ol_MapBrowserEvent, ol_Observable_unByKey, ol_Pixel, ol_source, ol_style, ol_geom, ol_interaction, ol_layer } from "@omnia/exports/openlayers";


export class DrawMarginInteraction extends ol_interaction.Pointer {

    private _map: ol_Map;
    private _active: boolean;

    private _geometryFunction: (line: ol_geom.LineString) => ol_geom.Geometry;

    private _clickEventDispose: Function;
    private _keydownEventDispose: Function;

    private _overlay: ol_VectorLayer;
    private _dragPointFeature: ol_Feature;

    private _selectionHighlightFeature: ol_Feature;

    private _targetFeature: ol_Feature;
    private _targetFeatureBoundary: ol_Coordinate[];
    private _targetFeatureCenter: ol_Coordinate;
    private _targetHighlightFeature: ol_Feature;

    private _point1: ol_Coordinate;
    private _point1Feature: ol_Feature;
    private _point1SegmentIndex: number;

    private _point2: ol_Coordinate

    private _fullBoundary: boolean;

    private _currentLine: ol_geom.LineString;

    private _direction: number;

    private _committedSelection: ol_geom.LineString;

    private _text: string


    constructor(private targetFeaturesSource: ol_source.Vector, private _fillColor?: any) {

        super()

        this.setActive(false);

        this.setupOverlay();

    }

    dispose() {

        const map = this.getMap()

        this.reset()
        this.setActive(false)


        if (map) {
            map.removeInteraction(this)
        }

    }

    private setupOverlay() {

        let white = [255, 255, 255, 0.5];

        this._dragPointFeature = new ol_Feature();

        this._selectionHighlightFeature = new ol_Feature();
        this.updateMarginStyle();

        this._targetHighlightFeature = new ol_Feature();
        this._targetHighlightFeature.setStyle(new ol_style.Style({

            stroke: new ol_style.Stroke({
                width: 3,
                color: "black",
            }),

            fill: new ol_style.Fill({
                color: white,
            }),

        }));


        this._point1Feature = new ol_Feature();

        this._overlay = new ol_layer.Vector({
            source: new ol_source.Vector({
                useSpatialIndex: false,
                features: [
                    this._dragPointFeature,
                    this._selectionHighlightFeature,
                    this._targetHighlightFeature,
                    this._point1Feature
                ],
            }),
            updateWhileAnimating: true,
            updateWhileInteracting: true,
            style: getDefaultEditStylePoint()
        });

    }

    public setMap(map: ol_Map) {

        this._overlay.setMap(map);
        (<any>ol_interaction.Pointer.prototype).setMap.call(this, map);

        this._map = map;

        if (this._active) {
            this.setupClickListener(map);
        }

        this.setupKeydownEventListener(map ? map.getTargetElement() : null);

    }

    public setActive(active: boolean) {

        this.setupClickListener(active ? this._map : null);

        this._active = active;
        this._fullBoundary = false;

        if (this._point1Feature) {
            this._point1Feature.setStyle(null);
            this._dragPointFeature.setStyle(null);
        }

        super.setActive(active);

    }

    private setupClickListener(map: ol_Map) {

        if (this._clickEventDispose) {
            this._clickEventDispose();
            this._clickEventDispose = null;
        }

        if (map) {

            let key = map.on("click", e => this.handleClick(<ol_MapBrowserEvent>e));

            this._clickEventDispose = () => {
                ol_Observable_unByKey(key);
            };

        }

    }

    private setupKeydownEventListener(elt: Element) {

        if (this._keydownEventDispose) {
            this._keydownEventDispose();
            this._keydownEventDispose = null;
        }

        if (elt) {

            let listener = (event: KeyboardEvent) => {

                if (event.key == "Escape") {
                    this.resetCurrentStep();
                }

            };

            elt.addEventListener("keydown", listener);

            this._keydownEventDispose = () => {
                elt.removeEventListener("keydown", listener);
            }

        }

    }

    isFirstPointSet() {
        return !!this._point1
    }

    get selection(): ol_geom.LineString {
        return this._committedSelection;
    }

    getCurrentSelection() {
        return this._fullBoundary
            ? this.getFullBoundaryAsLine()
            : this._point2 && this.getSelectedBoundaryPart(this._point2)
    }

    getCurrentSelectionGeometry() {
        return this._selectionHighlightFeature.getGeometry()
    }

    get targetFeature(): ol_Feature {
        return this._targetFeature;
    }

    get geometryFunction(): (line: ol_geom.LineString) => ol_geom.Geometry {
        return this._geometryFunction;
    }

    set geometryFunction(value: (line: ol_geom.LineString) => ol_geom.Geometry) {

        this._geometryFunction = value;

        if (this._targetFeature) {
            this.highlightSelection(this._point2);
        }

    }

    get bufferFillColor() {
        return this._fillColor
    }

    get text() {
        return this._text
    }

    setMarginStyle(style: { color: any, text: string }) {
        this._fillColor = style.color
        this._text = style.text
        this.updateMarginStyle()
    }

    public refresh() {

        if (this._fullBoundary) {
            this.setupFullBoundary();
        }
        else if (this._point2) {

            this.highlightSelection(this._point2);

            if (this.selection) {
                this.commitSelection(this._point2);
            }

        }
    }

    public resetCurrentStep() {

        if (this._committedSelection) {
            this._committedSelection = null;
        }
        else if (this._targetFeature) {
            this.cleanup();
        }

    }

    public reset() {

        this.cleanup();

    }

    private cleanup() {

        this._committedSelection = null;

        this._targetFeature = null;
        this._targetFeatureBoundary = null;
        this._targetFeatureCenter = null;
        this._targetHighlightFeature.setGeometry(null);
        this._selectionHighlightFeature.setGeometry(null);
        this._currentLine = null;

        this._point1 = null;
        this._point2 = null;
        this._point1SegmentIndex = null;
        this._point1Feature.setGeometry(null);

        this._dragPointFeature.setGeometry(null);

        this._direction = null;

    }


    protected handleMoveEvent(evt: ol_MapBrowserEvent) {

        if (!this._active) {
            return;
        }

        if (this._committedSelection) {
            return;
        }

        let point: ol_geom.Point = null;
        let closest = this.getClosest(evt);

        if (closest) {

            point = new ol_geom.Point(closest.coord)

            if (this._targetFeature) {

                this._point2 = evt.coordinate
                this.highlightSelection(closest.coord);

                this.dispatchEvent("move")

            }

        }

        this._dragPointFeature.setGeometry(point);

    }

    private handleClick(evt: ol_MapBrowserEvent) {

        if (this._committedSelection) {
            return;
        }

        let closest = this.getClosest(evt);
        if (!closest) return;

        if (this._targetFeature) {
            this.commitSelection(closest.coord);
        }

        if (!this._targetFeature) {

            this.setupFirstPoint(closest);

            this.dispatchEvent("change");

        }

    }

    protected handleDownEvent(evt: ol_MapBrowserEvent): boolean {

        if (this._committedSelection && this._point1 && this._point2 && (!this._fullBoundary)) {

            let map = evt.map;

            if (this.checkDistanceFromCoordPx(evt.pixel, map, this._point2)) {
                return true;
            }

            if (this.checkDistanceFromCoordPx(evt.pixel, map, this._point1)) {
                this.switchPoints();
                return true;
            }

        }

        return false;

    }

    protected handleUpEvent(evt: ol_MapBrowserEvent): boolean {
        this.commitSelection(this._point2);
        return false;
    }

    private checkDistanceFromCoordPx(px: ol_Pixel, map: ol_Map, coord: ol_Coordinate, max: number = 15) {

        let px1 = map.getPixelFromCoordinate(coord);

        let dist = this.getDistanceSquared(px1, px);

        return (dist <= (max * max));

    }

    public switchDirection() {

        if (this._direction === null) {
            const angle = calculateAngle(this._targetFeatureCenter, this._point1, this._point2) - Math.PI;
            this._direction = angle < 0 ? -1 : 1;
        }

        this._direction = -this._direction;

        this.highlightSelection(this._point2);

        if (this.selection) {
            this.commitSelection(this._point2);
        }

    }

    private switchPoints() {

        let p1 = this._point2;

        this._point2 = this._point1;

        this._point1SegmentIndex = this.getSegmentIndexOnTarget(p1);
        this._point1 = p1;

        this._point1Feature.setGeometry(new ol_geom.Point(this._point1));
        this._dragPointFeature.setGeometry(new ol_geom.Point(this._point2));

        if (this._direction !== null) {
            this._direction = -this._direction;
        }

    }


    handleDragEvent(evt: ol_MapBrowserEvent) {

        let closest = this.getClosest(evt);

        if (closest && this._targetFeature) {
            this._point2 = closest.coord;
            this._dragPointFeature.setGeometry(new ol_geom.Point(this._point2));
            this.highlightSelection(closest.coord);
            this.dispatchEvent("change");
        }

    }

    private commitSelection(point2: ol_Coordinate) {

        let line = this.getSelectedBoundaryPart(point2);

        this._committedSelection = line;
        this._point2 = point2;

        this._dragPointFeature.setStyle(null);

        this.dispatchEvent("change");

    }

    private highlightSelection(point2: ol_Coordinate) {

        let line = this.getSelectedBoundaryPart(point2);
        if (!line) return;

        this._currentLine = line;

        let geometry: ol_geom.Geometry = line;

        if (this._geometryFunction) {
            geometry = this._geometryFunction(line);
        }

        this._selectionHighlightFeature.setGeometry(geometry);

    }

    public setInitialState(line: ol_geom.LineString): boolean {

        let coords = line.getCoordinates();

        let p1 = coords[0];
        let p2 = coords[coords.length - 1];

        let closest = this.getClosestFromCoordinate(this._map, p1);

        if (closest) {

            let full = (p1[0] == p2[0]) && (p1[1] == p2[1]);

            this.setupFirstPoint(closest);

            let center = ol_extent.getCenter(closest.feature.getGeometry().getExtent());
            let angle = calculateAngle(center, coords[0], coords[1]) - Math.PI;

            this._point2 = p2;
            this._dragPointFeature.setGeometry(new ol_geom.Point(p2));

            this._direction = (angle < 0 ? -1 : 1);

            this.highlightSelection(p2);
            this.commitSelection(p2);

            this._point1Feature.setStyle(null);
            this._dragPointFeature.setStyle(null);

            this.setFullBoundaryMargin(full);

            return true;

        }

    }

    get fullBoundary() {
        return this._fullBoundary
    }

    public setFullBoundaryMargin(full: boolean) {

        this._fullBoundary = full;

        this._point1Feature.setStyle(null);
        this._dragPointFeature.setStyle(null);

        if (!this._point1) return;

        if (full) {
            this.setupFullBoundary()
        }
        else {

            if (!this._point2) {
                return
            }

            this.highlightSelection(this._point2);
            this.commitSelection(this._point2);

        }

        this.dispatchEvent("change");

    }

    private setupFullBoundary() {

        let line = this.getFullBoundaryAsLine();
        this._committedSelection = line;
        this._currentLine = line;

        let geometry: ol_geom.Geometry = line;

        if (this._geometryFunction) {
            geometry = this._geometryFunction(line);
        }

        this._selectionHighlightFeature.setGeometry(geometry);

        this._point1Feature.setStyle([]);
        this._dragPointFeature.setStyle([]);

    }


    private setupFirstPoint(closest: { feature: ol_Feature, coord: ol_Coordinate }) {

        this._targetFeature = closest.feature;

        let geom = (<any>closest.feature.getGeometry());

        this._targetFeatureBoundary = geom.getCoordinates(false)[0];
        this._targetFeatureCenter = geom.getInteriorPoint();
        this._targetFeatureCenter = ol_extent.getCenter(geom.getExtent());

        this._direction = null;

        this._point1 = closest.coord;
        this._point1SegmentIndex = this.getSegmentIndexOnTarget(closest.coord);

        this._point1Feature.setGeometry(new ol_geom.Point(closest.coord));

    }

    private getFullBoundaryAsLine() {

        let coords = this._targetFeatureBoundary.map(c => c.slice());;

        let line = new ol_geom.LineString(coords);

        return line;

    }


    private getSelectedBoundaryPart(point2: ol_Coordinate): ol_geom.LineString {

        let index = this.getSegmentIndexOnTarget(point2);

        if (index >= 0) {

            let i1 = this._point1SegmentIndex;
            let i2 = index;
            let coords = [this._point1];

            let direction = this._direction;

            let lastIndex = this._targetFeatureBoundary.length - 2;

            if (direction && (i1 == i2)) {

                if (this.shouldClearDirection(point2, i1, direction)) {
                    this._direction = direction = null;
                }

            }

            if (!direction) {

                if ((!i1) && (i2 == lastIndex)) {
                    this._direction = direction = -1;
                }
                else if ((!i2) && (i1 == lastIndex)) {
                    this._direction = direction = 1;
                }
                else if (i1 != i2) {
                    let d = (i2 - i1);
                    this._direction = direction = d / Math.abs(d);
                }

            }


            if (direction) {

                let i = i1;

                do {

                    if (direction > 0) i = this.getPointIndex(i + direction);

                    coords.push(this._targetFeatureBoundary[i]);

                    if (direction < 0) i = this.getPointIndex(i + direction);

                } while (i != i2)

            }

            coords.push(point2);

            let line = new ol_geom.LineString(coords);

            return line;

        }

    }

    private shouldClearDirection(p2: ol_Coordinate, index: number, direction: number): boolean {

        let ni = this.getPointIndex(index + (direction < 0 ? 0 : 1));
        let np = this._targetFeatureBoundary[ni];

        let d1 = this.getDistanceSquared(this._point1, np);
        let d2 = this.getDistanceSquared(p2, np);

        return (d2 < d1);

    }

    private getPointIndex(index: number): number {
        if (index < 0) index += this._targetFeatureBoundary.length;
        index = index % this._targetFeatureBoundary.length;
        return index;
    }

    private getClosest(evt: ol_MapBrowserEvent): { feature: ol_Feature, coord: ol_Coordinate } {

        return this.getClosestFromCoordinate(evt.map, evt.coordinate);

    }

    private getClosestFromCoordinate(map: ol_Map, sourceCoordinate: ol_Coordinate): { feature: ol_Feature, coord: ol_Coordinate } {

        let closest: ol_Feature = this._targetFeature;
        let step2 = !!closest;

        if (!closest) {

            const features = this.targetFeaturesSource.getFeaturesAtCoordinate(sourceCoordinate)

            closest = features?.length === 1
                ? features[0]
                : this.targetFeaturesSource.getClosestFeatureToCoordinate(sourceCoordinate)

        }

        if (closest) {

            let geom = closest.getGeometry();

            let coord = geom.getClosestPoint(sourceCoordinate);

            let px1 = map.getPixelFromCoordinate(coord);
            let px2 = map.getPixelFromCoordinate(sourceCoordinate);

            let dist = this.getDistanceSquared(px1, px2);

            if (step2 || (dist <= 2000)) {

                this._targetHighlightFeature.setGeometry(geom);

                return { feature: closest, coord: coord };

            }

        }

        this._targetHighlightFeature.setGeometry(null);

    }


    protected getDistanceSquared(p1: ol_Coordinate, p2: ol_Coordinate): number {

        let dx = p1[0] - p2[0];
        let dy = p1[1] - p2[1];

        return (dx * dx) + (dy * dy);

    }

    private getSegmentIndexOnTarget(point: ol_Coordinate): number {

        if (!this._targetFeatureBoundary) {
            return - 1;
        }

        let segments = this._targetFeatureBoundary.map((c1: ol_Coordinate, index: number) => {

            if (index < (this._targetFeatureBoundary.length - 1)) {

                let c2 = this._targetFeatureBoundary[index + 1];

                let segment = new ol_geom.LineString([c1, c2]);
                let closest = segment.getClosestPoint(point);
                let dist = this.getDistanceSquared(point, closest);

                return {
                    index: index,
                    dist: dist,
                }
            }


        })
            .filter(o => !!o)
            .sort((i1, i2) => i1.dist - i2.dist);

        if (segments.length) {
            return segments[0].index;
        }

        return -1;

    }

    private updateMarginStyle() {
        const styles = this.getBufferStyle()
        this._selectionHighlightFeature.setStyle(styles)
    }

    private getBufferStyle() {

        const outer = getBaseEditStyle(this._fillColor || "white")

        const inner = new ol_style.Style({

            geometry: g => this._currentLine,

            zIndex: 999,

            fill: new ol_style.Fill({
                color: "white"
            }),

            stroke: new ol_style.Stroke({
                color: "#000",
                width: 2,
                lineDash: [5, 10],

            })

        })

        const styles = outer
            .concat(inner)
            .concat([this.getBufferTextStyle()].filter(s => !!s))

        return styles

    }

    private getBufferTextStyle() {

        if (!this._text) null

        const text = new ol_style.Text(getTextBaseStyle())

        text.setText(this._text)
        text.setFont("14px 'Open Sans',Arial,sans-serif")

        const style = new ol_style.Style({
            geometry: g => this._currentLine,
            text: text,
            zIndex: 1500
        })

        return style

    }
}
